/**
 * Claims Configuration -> Registration tab.
 *
 * Curates, per claim schema, which claim attributes are captured during
 * claim creation - independently for Admin creation and Consumer creation.
 * Each surface is an ordered, drag-reorderable list of fields with an
 * optional display label override and a required/optional toggle.
 *
 * Fields are NOT deletable here - the real attributes live on the Claim
 * Schema. This tab only selects, orders, relabels, and sets required-ness.
 *
 * Source of truth: the CREATION WorkflowAction's
 * ``ui_hints.capture_fields`` (admin) and ``ui_hints.consumer_capture_fields``
 * (consumer). The label override is metadata only - it never mutates the
 * underlying ClaimAttribute.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ClipboardList, FileText, GripVertical, Pencil, Plus, X } from 'lucide-react'
import { toast } from 'sonner'

import {
  useClaimSchemas,
  type ClaimAttribute,
  type ClaimSchemaRow,
} from '@/admin/hooks/use-claim-schemas'
import {
  useClaimWorkflows,
  useClaimWorkflow,
  useUpdateWorkflowAction,
  type CaptureFieldEntry,
  type WorkflowActionDef,
} from '@/admin/hooks/use-claim-workflows'
import { MasterDetail } from '@/admin/components/shared/master-detail'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Switch } from '@/shared/components/ui/switch'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import {
  SearchableSelect,
  type SearchableSelectGroup,
} from '@/shared/components/ui/searchable-select'
import { toastApiError } from '@/shared/lib/api-error'

// ── Page ──────────────────────────────────────────────────

export function ClaimRegistrationPage() {
  const { data: schemas, isLoading, isError } = useClaimSchemas()
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)

  const selectedSchema =
    (schemas ?? []).find((s) => s.uuid === selectedUuid) ?? null

  return (
    <MasterDetail
      testId="claim-registration"
      title="Claim Schemas"
      hasSelection={!!selectedUuid}
      selectedId={selectedUuid}
      visibleItemIds={(schemas ?? []).map((s) => s.uuid)}
      onSelectionChange={setSelectedUuid}
      onBack={() => setSelectedUuid(null)}
      list={
        isLoading ? (
          <p className="text-[12px] text-[var(--ink-3)]">Loading...</p>
        ) : isError ? (
          <p className="text-[12px] text-[var(--red)]">Failed to load schemas</p>
        ) : !(schemas ?? []).length ? (
          <p className="text-[12px] text-[var(--ink-3)]">No schemas yet</p>
        ) : (
          <div className="flex flex-col gap-0.5">
            {(schemas ?? []).map((schema) => {
              const isActive = schema.uuid === selectedUuid
              return (
                <Button
                  key={schema.uuid}
                  variant="ghost"
                  onClick={() => setSelectedUuid(schema.uuid)}
                  data-testid={`claim-registration-item-${schema.uuid}`}
                  className={`h-auto flex-col items-start gap-0.5 rounded-[4px] px-3 py-2 text-left text-[12px] ${
                    isActive
                      ? 'bg-[var(--green-bg-subtle)] text-[var(--green)] hover:bg-[var(--green-bg-subtle)] hover:text-[var(--green)]'
                      : 'text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]'
                  }`}
                >
                  <span className="flex w-full items-center gap-2">
                    <FileText
                      className={`h-3 w-3 shrink-0 ${
                        isActive ? 'text-[var(--green)]' : 'text-[var(--ink-3)]'
                      }`}
                    />
                    <span className="flex-1 truncate">{schema.name}</span>
                  </span>
                  <span className="ml-5 text-[10px] text-[var(--ink-3)]">
                    {schema.attributes.length} attribute
                    {schema.attributes.length !== 1 ? 's' : ''}
                  </span>
                </Button>
              )
            })}
          </div>
        )
      }
      detail={
        selectedSchema ? (
          <SchemaRegistrationDetail schema={selectedSchema} />
        ) : (
          <div className="flex justify-center pt-24">
            <p className="text-[13px] text-[var(--ink-3)]">
              Select a schema to configure its claim-creation fields
            </p>
          </div>
        )
      }
    />
  )
}

// ── Detail: resolve the schema's CREATION action and edit it ─────────

function SchemaRegistrationDetail({ schema }: { schema: ClaimSchemaRow }) {
  const { data: workflows, isLoading: workflowsLoading } = useClaimWorkflows()

  const workflowUuid = useMemo(
    () =>
      (workflows ?? []).find((w) => w.claim_schema_uuid === schema.uuid)?.uuid ??
      null,
    [workflows, schema.uuid],
  )

  const { data: workflow, isLoading: detailLoading } =
    useClaimWorkflow(workflowUuid)

  const creationAction = useMemo<WorkflowActionDef | null>(
    () =>
      (workflow?.actions ?? []).find((a) => a.applicability === 'CREATION') ??
      null,
    [workflow],
  )

  if (workflowsLoading || (workflowUuid && detailLoading)) {
    return <p className="text-[12px] text-[var(--ink-3)]">Loading...</p>
  }

  if (!workflowUuid || !creationAction) {
    return (
      <div className="flex flex-col items-center gap-2 pt-24 text-center">
        <ClipboardList className="h-5 w-5 text-[var(--ink-3)]" />
        <p className="max-w-sm text-[13px] text-[var(--ink-3)]">
          This schema has no claim workflow with a creation step yet. Create a
          claim workflow for it on the Claim Workflows tab first.
        </p>
      </div>
    )
  }

  return (
    <RegistrationEditor
      schema={schema}
      workflowUuid={workflowUuid}
      action={creationAction}
    />
  )
}

// ── Editor: Admin / Consumer tabs ────────────────────────────────────

function RegistrationEditor({
  schema,
  workflowUuid,
  action,
}: {
  schema: ClaimSchemaRow
  workflowUuid: string
  action: WorkflowActionDef
}) {
  const updateAction = useUpdateWorkflowAction()

  const attributes = useMemo(
    () => [...schema.attributes].sort((a, b) => a.order - b.order),
    [schema.attributes],
  )

  // Resolve each entry's ``required`` to a concrete boolean from the
  // attribute's own default when the stored value is null/undefined
  // (legacy bare-string entries inherit). This keeps "what you see is
  // what you save" - without it, a never-toggled field whose attribute
  // is required would be silently flipped to optional on the first save.
  const hydrateEntries = useMemo(() => {
    const requiredByCode: Record<string, boolean> = {}
    for (const a of attributes) requiredByCode[a.code_name] = a.required
    return (entries: CaptureFieldEntry[]): CaptureFieldEntry[] =>
      entries.map((e) => ({
        code_name: e.code_name,
        label: e.label ?? '',
        required: e.required ?? requiredByCode[e.code_name] ?? false,
      }))
  }, [attributes])

  const [adminEntries, setAdminEntries] = useState<CaptureFieldEntry[]>([])
  const [consumerEntries, setConsumerEntries] = useState<CaptureFieldEntry[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const loadedActionRef = useRef<string | null>(null)

  // Sync drafts from the resolved action. Always reset when the action
  // changes (schema switch). For background refetches of the SAME action
  // (React Query refetchOnWindowFocus), only re-sync when there are no
  // unsaved edits - otherwise a refetch would silently discard the
  // user's in-progress changes.
  useEffect(() => {
    const isNewAction = loadedActionRef.current !== action.uuid
    if (!isNewAction && isDirty) return
    setAdminEntries(hydrateEntries(action.capture_fields ?? []))
    setConsumerEntries(hydrateEntries(action.consumer_capture_fields ?? []))
    loadedActionRef.current = action.uuid
    if (isNewAction) setIsDirty(false)
  }, [
    action.uuid,
    action.capture_fields,
    action.consumer_capture_fields,
    hydrateEntries,
    isDirty,
  ])

  function updateAdmin(next: CaptureFieldEntry[]) {
    setAdminEntries(next)
    setIsDirty(true)
  }
  function updateConsumer(next: CaptureFieldEntry[]) {
    setConsumerEntries(next)
    setIsDirty(true)
  }

  async function handleSave() {
    try {
      await updateAction.mutateAsync({
        workflowUuid,
        actionUuid: action.uuid,
        data: {
          ui_hints: {
            capture_fields: serialiseEntries(adminEntries),
            consumer_capture_fields: serialiseEntries(consumerEntries),
          },
        },
      })
      setIsDirty(false)
      toast.success('Registration fields saved')
    } catch (err) {
      toastApiError(err, 'Failed to save registration fields')
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-medium text-[var(--ink)]">
            Claim creation fields
          </h2>
          <p className="mt-1 max-w-lg text-[12px] text-[var(--ink-3)]">
            Choose which {schema.name} attributes are captured when a claim is
            created, set their order, relabel them, and mark each required or
            optional. Configure Admin and Consumer creation separately.
          </p>
        </div>
        <Button
          variant="default"
          onClick={handleSave}
          disabled={!isDirty || updateAction.isPending}
          data-testid="claim-registration-save"
          className="shrink-0 rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-40"
        >
          {updateAction.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <Tabs defaultValue="admin">
        <TabsList variant="line">
          <TabsTrigger value="admin">Admin creation</TabsTrigger>
          <TabsTrigger value="consumer">Consumer creation</TabsTrigger>
        </TabsList>
        <TabsContent value="admin">
          <CaptureFieldList
            surface="admin"
            attributes={attributes}
            entries={adminEntries}
            onChange={updateAdmin}
          />
        </TabsContent>
        <TabsContent value="consumer">
          <CaptureFieldList
            surface="consumer"
            attributes={attributes}
            entries={consumerEntries}
            onChange={updateConsumer}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ── Capture-field list (drag-reorder; relabel + required in a modal) ──

function CaptureFieldList({
  surface,
  attributes,
  entries,
  onChange,
}: {
  surface: 'admin' | 'consumer'
  attributes: ClaimAttribute[]
  entries: CaptureFieldEntry[]
  onChange: (next: CaptureFieldEntry[]) => void
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const [editingCode, setEditingCode] = useState<string | null>(null)

  const attrByCode = useMemo(() => {
    const map: Record<string, ClaimAttribute> = {}
    for (const a of attributes) map[a.code_name] = a
    return map
  }, [attributes])

  // Only entries whose attribute still exists are editable here.
  const visibleEntries = entries.filter((e) => attrByCode[e.code_name])

  const availableGroups: SearchableSelectGroup[] = useMemo(() => {
    const used = new Set(entries.map((e) => e.code_name))
    const options = attributes
      .filter((a) => !used.has(a.code_name))
      .map((a) => ({ value: a.code_name, label: a.display_name }))
    return options.length ? [{ label: 'Attributes', options }] : []
  }, [attributes, entries])

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = visibleEntries.findIndex((e) => e.code_name === active.id)
    const newIndex = visibleEntries.findIndex((e) => e.code_name === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    onChange(arrayMove(visibleEntries, oldIndex, newIndex))
  }

  function addField(codeName: string) {
    const attr = attrByCode[codeName]
    if (!attr) return
    onChange([
      ...visibleEntries,
      { code_name: codeName, label: '', required: attr.required },
    ])
  }

  function patchEntry(codeName: string, patch: Partial<CaptureFieldEntry>) {
    onChange(
      visibleEntries.map((e) =>
        e.code_name === codeName ? { ...e, ...patch } : e,
      ),
    )
  }

  function removeField(codeName: string) {
    onChange(visibleEntries.filter((e) => e.code_name !== codeName))
  }

  const editingEntry = visibleEntries.find((e) => e.code_name === editingCode)

  return (
    <div className="flex flex-col gap-3">
      {visibleEntries.length === 0 ? (
        <p className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] px-4 py-6 text-center text-[12px] text-[var(--ink-3)]">
          No fields captured during {surface} creation yet. Add one below.
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visibleEntries.map((e) => e.code_name)}
            strategy={verticalListSortingStrategy}
          >
            <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
              {visibleEntries.map((entry, i) => (
                <CaptureFieldRow
                  key={entry.code_name}
                  entry={entry}
                  attr={attrByCode[entry.code_name]}
                  isFirst={i === 0}
                  onEdit={() => setEditingCode(entry.code_name)}
                  onRemove={() => removeField(entry.code_name)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {availableGroups.length > 0 && (
        <div className="flex items-center gap-2">
          <Plus className="h-3.5 w-3.5 text-[var(--ink-3)]" />
          <div className="w-64">
            <SearchableSelect
              value=""
              groups={availableGroups}
              placeholder="Add a field..."
              searchPlaceholder="Search attributes..."
              onValueChange={addField}
              data-testid={`claim-registration-add-${surface}`}
            />
          </div>
        </div>
      )}

      {editingEntry ? (
        <CaptureFieldEditDialog
          key={editingEntry.code_name}
          entry={editingEntry}
          attr={attrByCode[editingEntry.code_name]}
          onClose={() => setEditingCode(null)}
          onSave={(label, required) =>
            patchEntry(editingEntry.code_name, { label, required })
          }
        />
      ) : null}
    </div>
  )
}

function CaptureFieldRow({
  entry,
  attr,
  isFirst,
  onEdit,
  onRemove,
}: {
  entry: CaptureFieldEntry
  attr: ClaimAttribute
  isFirst: boolean
  onEdit: () => void
  onRemove: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: entry.code_name })

  // The label IS the attribute's display name until a custom one is set.
  const customLabel = (entry.label ?? '').trim()
  const displayedLabel = customLabel || attr.display_name
  const required = entry.required ?? attr.required

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={`flex items-center gap-3 bg-[var(--bg)] px-3 py-2.5 ${
        isFirst ? '' : 'border-t-[0.5px] border-t-[var(--rule)]'
      } ${isDragging ? 'opacity-50' : ''}`}
    >
      <div
        className="shrink-0 cursor-grab touch-none text-[var(--ink-3)] active:cursor-grabbing"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13px] text-[var(--ink)]">{displayedLabel}</span>
          {customLabel && customLabel !== attr.display_name ? (
            <Badge variant="ghost" className="text-[10px]">
              {attr.display_name}
            </Badge>
          ) : null}
          <Badge variant="outline" className="font-mono text-[10px]">
            {attr.code_name}
          </Badge>
          <Badge variant="secondary" className="text-[10px] uppercase tracking-[0.08em]">
            {attr.field_type.toLowerCase().replace(/_/g, ' ')}
          </Badge>
        </div>
        <p className="mt-0.5 truncate text-[10px] text-[var(--ink-3)]">
          Claim attribute.
        </p>
      </div>

      <Badge
        variant={required ? 'secondary' : 'ghost'}
        className="shrink-0 text-[10px]"
      >
        {required ? 'Required' : 'Optional'}
      </Badge>
      <Button type="button" variant="ghost" size="icon-xs" onClick={onEdit}>
        <Pencil size={13} />
      </Button>
      <Button type="button" variant="ghost" size="icon-xs" onClick={onRemove}>
        <X size={13} />
      </Button>
    </div>
  )
}

function CaptureFieldEditDialog({
  entry,
  attr,
  onClose,
  onSave,
}: {
  entry: CaptureFieldEntry
  attr: ClaimAttribute
  onClose: () => void
  onSave: (label: string, required: boolean) => void
}) {
  const [label, setLabel] = useState((entry.label ?? '').trim())
  const [required, setRequired] = useState<boolean>(
    entry.required ?? attr.required,
  )

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Edit field</DialogTitle>
          <DialogDescription>
            {attr.display_name} · {attr.code_name}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="capture-field-label">Label</Label>
            <Input
              id="capture-field-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={attr.display_name}
            />
            <p className="text-[11px] text-[var(--ink-3)]">
              Shown on the claim creation form. Defaults to "{attr.display_name}".
            </p>
          </div>
          <Label className="flex cursor-pointer items-center gap-2">
            <Switch checked={required} onCheckedChange={setRequired} size="sm" />
            <span className="text-[12px] text-[var(--ink-1)]">
              {required ? 'Required' : 'Optional'}
            </span>
          </Label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={() => {
              onSave(label.trim(), required)
              onClose()
            }}
            className="bg-[var(--green)] text-[var(--bg)] hover:bg-[var(--green-hover)]"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Helpers ────────────────────────────────────────────────

/**
 * Serialise draft entries for persistence: drop empty labels (so the
 * attribute's own display_name applies) and keep ``required`` explicit.
 */
function serialiseEntries(entries: CaptureFieldEntry[]): CaptureFieldEntry[] {
  return entries.map((e) => {
    const label = (e.label ?? '').trim()
    return {
      code_name: e.code_name,
      ...(label ? { label } : {}),
      required: !!e.required,
    }
  })
}
